import { useState, useEffect, useRef } from "react";

const BOOKING_DURATION_MS = 15 * 60 * 1000; // 15 minutes

type CountdownTimerProps = {
  createdAt: string;
  onExpired?: () => void;
};

export function CountdownTimer({ createdAt, onExpired }: CountdownTimerProps) {
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  const getRemainingMs = () => {
    const elapsed = Date.now() - new Date(createdAt).getTime();
    return Math.max(0, BOOKING_DURATION_MS - elapsed);
  };

  const [remainingMs, setRemainingMs] = useState(getRemainingMs);

  useEffect(() => {
    if (remainingMs <= 0) {
      onExpiredRef.current?.();
      return;
    }

    const interval = setInterval(() => {
      const remaining = getRemainingMs();
      setRemainingMs(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        onExpiredRef.current?.();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [createdAt]);

  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (remainingMs <= 0) {
    return null;
  }

  // Couleurs selon le temps restant
  const isUrgent = remainingMs < 60 * 1000; // < 1 min
  const isWarning = remainingMs < 5 * 60 * 1000; // < 5 min

  const colorClasses = isUrgent
    ? "bg-red-50 text-red-700 border-red-200"
    : isWarning
      ? "bg-orange-50 text-orange-700 border-orange-200"
      : "bg-blue-50 text-blue-700 border-blue-200";

  const iconColor = isUrgent
    ? "text-red-500"
    : isWarning
      ? "text-orange-500"
      : "text-blue-500";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border ${colorClasses} ${isUrgent ? "animate-pulse" : ""}`}
    >
      <svg
        className={`size-3 ${iconColor}`}
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="2"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
      {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
    </span>
  );
}
